"use client";

import {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { api, ApiError, apiErrorMessage } from "@/lib/api";
import { CodeInput } from "@/app/(auth)/_components/CodeInput";
import { StrengthMeter } from "@/app/(auth)/_components/AuthShell";
// The form primitives (.lpa-input, .lpa-otp, .lpa-or, .lpa-alert, …) are the
// same ones the auth routes use. Imported rather than duplicated so the two
// surfaces cannot drift apart.
import "@/app/(auth)/auth.css";
import "./login-modal.css";
import { lpFontVars } from "./fonts";
import { Logo } from "./Logo";

// Authentication as an overlay on the marketing site, not a destination.
//
// Both halves live here — logging in AND signing up — because they are two
// answers to one question, and a visitor who picks wrong must be able to swap
// without losing the page behind them or the address they already typed.
// Switching is local state, not navigation.
//
// The whole page stays behind it, blurred: the visitor never loses their
// place, and authenticating reads as a small step rather than a departure.
// `/sign-in` and `/sign-up` still exist as URLs and redirect here with
// `?login=1` / `?signup=1`, so every inbound link (the API client's 401
// bounce, the footer, bookmarks, the password-reset mail) keeps working.
//
// State lives in the URL for the same reason: it is shareable, survives a
// refresh, and lets Back close the dialog instead of leaving the site.

const LOGIN_KEY = "login";
const SIGNUP_KEY = "signup";
const DIALOG_KEYS = [LOGIN_KEY, SIGNUP_KEY] as const;

type Mode = "login" | "signup";

const emailSchema = z.object({
  email: z.string().email("Enter a valid email address."),
});
const passwordSchema = z.object({
  email: z.string().email("Enter a valid email address."),
  password: z.string().min(1, "Enter your password."),
});

const signupSchema = z
  .object({
    email: z.string().email("Enter a valid email address."),
    password: z.string().min(6, "Password must be at least 6 characters."),
    passwordConfirm: z.string().min(6, "Confirm your password."),
    acceptTerms: z.literal(true, {
      message: "You must accept the Terms of Service.",
    }),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    path: ["passwordConfirm"],
    message: "Passwords must match.",
  });

type EmailValues = z.infer<typeof emailSchema>;
type PasswordValues = z.infer<typeof passwordSchema>;
type SignupValues = z.infer<typeof signupSchema>;

type SessionResponse =
  | {
      requires_2fa?: false;
      access_token: string;
      refresh_token: string;
      user: { email: string; role: string };
    }
  | { requires_2fa: true; challenge_token: string };

type Stage = "email" | "code" | "password" | "expired";

/**
 * Open the dialog from anywhere without prop-drilling a callback.
 *
 * Writes `?login=1` through the NATIVE history API rather than router.push:
 * Next's App Router syncs `useSearchParams()` from `history.pushState`, so the
 * dialog opens instantly with no RSC round-trip, and the URL stays the single
 * source of truth (no second "is it open" state to keep in sync). Pushing —
 * not replacing — means the browser Back button closes the dialog instead of
 * leaving the site. The marker on the history entry is what lets `close()`
 * tell "I opened this" from "someone landed here with ?login=1 already".
 */
function openAuthModal(mode: Mode) {
  const key = mode === "signup" ? SIGNUP_KEY : LOGIN_KEY;
  const url = new URL(window.location.href);
  if (url.searchParams.get(key) === "1") return;
  for (const k of DIALOG_KEYS) url.searchParams.delete(k);
  url.searchParams.set(key, "1");
  window.history.pushState({ lpmOpenedHere: true }, "", url);
}

export function openLoginModal() {
  openAuthModal("login");
}

export function openSignupModal() {
  openAuthModal("signup");
}

/**
 * A dedicated, component-owned portal host appended to <body>.
 *
 * WHY NOT portal straight into document.body: the App Router (Turbopack in
 * Next 16) manages document.body's children directly across navigations. A
 * portal mounted into body puts React-owned nodes as *siblings* of nodes the
 * router moves and removes. When the modal later unmounts — or a <Link> inside
 * it triggers a transition — React tries to removeChild a node that the router
 * has already relocated, and the browser throws:
 *   "Failed to execute 'removeChild' on 'Node': The node to be removed is not
 *    a child of this node."
 *
 * Giving the portal its OWN <div> child of body means React only ever operates
 * inside a node it created, never racing the router for body's children.
 *
 * The host element is created lazily (once, browser-only) and the effect does
 * nothing but attach it to <body> and detach it on unmount — so there is no
 * setState inside the effect (react-hooks/set-state-in-effect).
 */

// Whether we are running in the browser. Modelled as an external store so the
// server snapshot reports "not hydrated" without copying anything into state
// from a mount effect — the same pattern AuthGuard uses.
const subscribeNoop = () => () => {};
function useIsClient() {
  return useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false,
  );
}

function usePortalHost(): HTMLElement | null {
  const isClient = useIsClient();
  // Lazily create the host once. Guarded for SSR: the initializer only runs on
  // the client because the component short-circuits before this hook matters,
  // but typeof-guard it anyway so a server render can never touch `document`.
  // The host IS the overlay root as far as the CSS is concerned: it is the
  // direct child of <body>, so it must carry `.lpm-root` or the page-blur rule
  // (`body > *:not(.lpm-root)`) would blur the dialog along with the page and
  // kill its pointer events. Set here, at creation, so nothing is mutated after
  // it leaves useState (react-hooks/immutability).
  const [host] = useState<HTMLElement | null>(() => {
    if (typeof document === "undefined") return null;
    const el = document.createElement("div");
    el.className = "lpm-root";
    el.setAttribute("data-lpm-portal", "");
    return el;
  });

  useEffect(() => {
    if (!host) return;
    document.body.appendChild(host);
    return () => {
      // Guard the removal too: if the router already detached it, don't throw.
      if (host.parentNode) host.parentNode.removeChild(host);
    };
  }, [host]);

  return isClient ? host : null;
}

export function LoginModal() {
  return (
    <Suspense fallback={null}>
      <LoginModalInner />
    </Suspense>
  );
}

function LoginModalInner() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const mode: Mode | null =
    searchParams.get(SIGNUP_KEY) === "1"
      ? "signup"
      : searchParams.get(LOGIN_KEY) === "1"
        ? "login"
        : null;
  const open = mode !== null;

  const close = useCallback(() => {
    if (!open) return;
    // If this history entry is the one openLoginModal() pushed, unwind it, so
    // the dialog leaves no trace in the Back stack. Otherwise (a direct hit on
    // /sign-in, a pasted ?login=1 link) there is nothing to go back TO, and
    // going back would leave the site — replace the URL in place instead.
    if (window.history.state?.lpmOpenedHere) {
      window.history.back();
      return;
    }
    const next = new URLSearchParams(searchParams.toString());
    for (const k of DIALOG_KEYS) next.delete(k);
    // Every flash param belongs to this dialog; leaving them behind would make
    // the banner reappear the next time it opens.
    for (const k of ["expired", "registered", "reset"]) next.delete(k);
    const qs = next.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [open, pathname, router, searchParams]);

  if (!open) return null;
  return (
    <LoginDialog
      key={mode}
      initialMode={mode}
      onClose={close}
      searchParams={searchParams}
    />
  );
}

function LoginDialog({
  initialMode,
  onClose,
  searchParams,
}: {
  initialMode: Mode;
  onClose: () => void;
  searchParams: URLSearchParams;
}) {
  const router = useRouter();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const portalHost = usePortalHost();

  const flashExpired = searchParams.get("expired") === "1";
  const flashRegistered = searchParams.get("registered") === "1";
  const flashReset = searchParams.get("reset") === "1";
  const nextParam = searchParams.get("next");
  // Same-origin paths only — an absolute URL here would be an open redirect.
  const nextPath = nextParam && /^\/(?!\/)/.test(nextParam) ? nextParam : null;

  // Mode is seeded from the URL, then owned locally: swapping login <-> signup
  // inside the dialog must not push a history entry or re-run the entrance
  // animation. The URL is only the ENTRY point.
  const [mode, setMode] = useState<Mode>(initialMode);
  // A timed-out session gets its OWN step in the dialog, not a banner stacked
  // above the email form: it is the first thing the visitor should read, so it
  // takes the whole card and hands off to the email stage on a single click.
  const [stage, setStage] = useState<Stage>(
    initialMode === "login" && flashExpired ? "expired" : "email",
  );
  const [email, setEmail] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Scroll lock + the page blur. The scrollbar's width is compensated so the
  // page behind does not jump sideways the instant the dialog opens, and
  // `data-lpm-open` on <html> is what login-modal.css hangs the blur off.
  useEffect(() => {
    const { body, documentElement } = document;
    const gap = window.innerWidth - documentElement.clientWidth;
    const prevOverflow = body.style.overflow;
    const prevPad = body.style.paddingRight;
    body.style.overflow = "hidden";
    if (gap > 0) body.style.paddingRight = `${gap}px`;
    documentElement.dataset.lpmOpen = "1";
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPad;
      delete documentElement.dataset.lpmOpen;
    };
  }, []);

  // Escape to dismiss, Tab kept inside the card, and focus restored to whatever
  // opened it — the three things that separate a dialog from a floating div.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !cardRef.current) return;
      const focusable = cardRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      opener?.focus?.();
    };
  }, [onClose]);

  const landing = useCallback(
    (role: string) => nextPath ?? (role === "admin" ? "/admin" : "/app"),
    [nextPath],
  );

  const completeSignIn = useCallback(
    (data: SessionResponse) => {
      if (data.requires_2fa) {
        sessionStorage.setItem("2fa_challenge_token", data.challenge_token);
        router.push("/sign-in/2fa");
        return;
      }
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      localStorage.setItem("user", JSON.stringify(data.user));
      router.push(landing(data.user.role));
    },
    [landing, router],
  );

  const goToStage = (next: Stage) => {
    setSubmitError(null);
    setNotice(null);
    setStage(next);
  };

  if (!portalHost) return null;

  const heading =
    mode === "signup"
      ? {
          title: "Create your account",
          sub: "One console for KVM servers, containers and functions — billed by the hour.",
        }
      : stage === "expired"
        ? {
            title: "You've been signed out",
            sub: "Your session expired. Please log in again.",
          }
        : stage === "code"
        ? {
            title: "Check your inbox",
            sub: `We sent a code to ${email}. It works for 10 minutes.`,
          }
        : stage === "password"
          ? { title: "Log in with a password", sub: "Enter your credentials." }
          : {
              // No brand name in the title: the logo sits directly above it,
              // so naming the product again just repeats what the eye already
              // read one line earlier.
              title: "Welcome back",
              sub: "Enter your email — we'll send a single-use code.",
            };

  /** Swap login <-> signup in place, carrying the typed address across. */
  const switchMode = (next: Mode, address?: string) => {
    setSubmitError(null);
    setNotice(null);
    setStage("email");
    if (address) setEmail(address);
    setMode(next);
  };

  return createPortal(
    <div className={`lp-root lpm-root ${lpFontVars}`}>
      {/* The scrim paints the tint; it does NOT take the click. The centring
          viewport below is also full-bleed and sits on top of it, so a handler
          here could never fire — dismiss-on-outside-click lives on the
          viewport, guarded by target === currentTarget so a click that lands
          on the card (or bubbles up from inside it) is ignored. */}
      <div className="lpm-scrim" aria-hidden="true" />
      <div
        className="lpm-viewport"
        role="presentation"
        onMouseDown={(e) => {
          // mousedown, not click: a drag that STARTS inside the card and ends
          // outside it (selecting the emailed code, say) would otherwise
          // register as an outside click and throw the dialog away.
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="lpm-card"
          ref={cardRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="lpm-title"
          aria-describedby="lpm-sub"
        >
          <button
            type="button"
            className="lpm-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path
                d="M2 2l10 10M12 2L2 12"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <header className="lpm-head">
            <span className="lpm-mark" aria-hidden="true">
              <Logo />
            </span>
            <h2 className="lpm-title" id="lpm-title">
              {heading.title}
            </h2>
            <p className="lpm-sub" id="lpm-sub">
              {heading.sub}
            </p>
          </header>

          {flashRegistered && mode === "login" && stage === "email" && (
            <Alert intent="success">Account created. Log in to continue.</Alert>
          )}
          {flashReset && mode === "login" && stage === "email" && (
            <Alert intent="success">
              Your password has been changed. Log in with the new one.
            </Alert>
          )}
          {notice && <Alert intent="success">{notice}</Alert>}
          {submitError && <Alert intent="error">{submitError}</Alert>}

          {mode === "signup" && (
            <SignupStage
              initialEmail={email}
              onCreated={(address) => {
                switchMode("login", address);
                setNotice("Account created. Log in to continue.");
              }}
              onSwitchToLogin={(address) => switchMode("login", address)}
              onError={setSubmitError}
            />
          )}

          {mode === "login" && stage === "expired" && (
            <ExpiredStage onContinue={() => goToStage("email")} />
          )}

          {mode === "login" && stage === "email" && (
            <EmailStage
              initialEmail={email}
              onCodeSent={(address) => {
                setEmail(address);
                goToStage("code");
              }}
              onUsePassword={(address) => {
                setEmail(address);
                goToStage("password");
              }}
              onSwitchToSignup={(address) => switchMode("signup", address)}
              onError={setSubmitError}
            />
          )}

          {mode === "login" && stage === "code" && (
            <CodeStage
              email={email}
              onSignedIn={completeSignIn}
              onError={setSubmitError}
              onNotice={setNotice}
              onChangeEmail={() => goToStage("email")}
              onUsePassword={() => goToStage("password")}
            />
          )}

          {mode === "login" && stage === "password" && (
            <PasswordStage
              initialEmail={email}
              onSignedIn={completeSignIn}
              onError={setSubmitError}
              onBack={(address) => {
                setEmail(address);
                goToStage("email");
              }}
            />
          )}

          {/* A quiet legal line — the enterprise sign-in convention: a
              reference to the terms that govern using the account, kept muted
              so it never competes with the form. */}
          <p className="lpm-legal">
            By continuing you agree to our{" "}
            <Link href="/terms" className="lpa-link lpa-link--muted">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link href="/privacy" className="lpa-link lpa-link--muted">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </div>,
    portalHost,
  );
}

/* -------------------------------------------------------------------------- */

function Alert({
  intent,
  children,
}: {
  intent: "success" | "error";
  children: ReactNode;
}) {
  return (
    <div
      className={`lpa-alert lpa-alert--${intent}`}
      role={intent === "error" ? "alert" : "status"}
    >
      {intent === "success" ? <AlertCheckIcon /> : <AlertWarnIcon />}
      <span>{children}</span>
    </div>
  );
}

/**
 * Session-expiry step: its own view of the dialog. The heading carries the
 * whole message, so the body is only the way forward.
 */
function ExpiredStage({ onContinue }: { onContinue: () => void }) {
  return (
    <button
      type="button"
      className="lp-btn lp-btn--lime lpa-submit"
      onClick={onContinue}
      autoFocus
    >
      Log in again
    </button>
  );
}

function AlertCheckIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" opacity="0.4" />
      <path
        d="M5 8.2 7 10.2 11 6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AlertWarnIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" opacity="0.4" />
      <path d="M8 4.6v4.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="8" cy="11.4" r="0.9" fill="currentColor" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 14 10" fill="none" aria-hidden="true">
      <path
        d="M1 5h12m0 0L9 1m4 4L9 9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2.2" y="4.2" width="15.6" height="11.6" rx="2.4" />
      <path d="m3.2 6 5.7 4.3a2 2 0 0 0 2.2 0L16.8 6" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg className="lpa-spinner" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M14.5 8A6.5 6.5 0 1 1 8 1.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EyeIcon({ off }: { off?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {off ? (
        <>
          <path d="M4 4l16 16" />
          <path d="M10.7 5.7A10 10 0 0 1 12 5.5c5.8 0 9.5 6.5 9.5 6.5a17.8 17.8 0 0 1-2.7 3.5M6.5 6.9C4 8.8 2.5 12 2.5 12S6.2 18.5 12 18.5a9.6 9.6 0 0 0 4-.9" />
          <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
        </>
      ) : (
        <>
          <path d="M2.5 12S6.2 5.5 12 5.5 21.5 12 21.5 12 17.8 18.5 12 18.5 2.5 12 2.5 12Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );
}

/* ---------------------------------------------------------------- stage 1 */

function EmailStage({
  initialEmail,
  onCodeSent,
  onUsePassword,
  onSwitchToSignup,
  onError,
}: {
  initialEmail: string;
  onCodeSent: (email: string) => void;
  onUsePassword: (email: string) => void;
  onSwitchToSignup: (email: string) => void;
  onError: (message: string | null) => void;
}) {
  const {
    register,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<EmailValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: initialEmail },
  });

  const requestCode = handleSubmit(async ({ email }) => {
    onError(null);
    try {
      await api("/api/v1/auth/email-code", { method: "POST", body: { email } });
      onCodeSent(email.trim().toLowerCase());
    } catch (err) {
      onError(
        err instanceof ApiError
          ? apiErrorMessage(err, "Could not send the code. Try again.")
          : "Could not connect to the server.",
      );
    }
  });

  const switchToPassword = async () => {
    onError(null);
    if (!(await trigger("email"))) return;
    onUsePassword(getValues("email").trim().toLowerCase());
  };

  return (
    <>
      <form onSubmit={requestCode} noValidate className="lpa-form">
        <div className="lpa-field">
          <label className="lpa-label" htmlFor="lpm-email">
            Email address
          </label>
          <input
            id="lpm-email"
            className="lpa-input"
            type="email"
            placeholder="you@company.com"
            autoComplete="email"
            autoFocus
            autoCapitalize="none"
            spellCheck={false}
            aria-label="Email address"
            aria-invalid={errors.email ? true : undefined}
            {...register("email")}
          />
          {errors.email && (
            <p className="lpa-error" role="alert">
              {errors.email.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          className="lp-btn lp-btn--lime lpa-submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Sending code…" : "Continue with email"}
          {isSubmitting ? <SpinnerIcon /> : <MailIcon />}
        </button>
      </form>

      <div className="lpa-or" role="separator" aria-orientation="horizontal">
        <span>or</span>
      </div>

      <button
        type="button"
        className="lp-btn lp-btn--ghost lpa-alt"
        onClick={switchToPassword}
      >
        Continue with a password
      </button>

      <footer className="lpm-foot">
        <p>
          <span className="lpa-foot-note">Don&apos;t have an account?</span>
          {/* A button, not a link: signing up swaps the dialog's contents and
              carries the address already typed. Navigating to /sign-up would
              throw both away. */}
          <button
            type="button"
            className="lpa-link"
            onClick={() => onSwitchToSignup(getValues("email").trim().toLowerCase())}
          >
            Create one
          </button>
        </p>
      </footer>
    </>
  );
}

/* ------------------------------------------------------------------ signup */

function SignupStage({
  initialEmail,
  onCreated,
  onSwitchToLogin,
  onError,
}: {
  initialEmail: string;
  onCreated: (email: string) => void;
  onSwitchToLogin: (email: string) => void;
  onError: (message: string | null) => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: initialEmail,
      password: "",
      passwordConfirm: "",
      acceptTerms: false as unknown as true,
    },
  });

  const acceptTerms = watch("acceptTerms");
  const passwordValue = watch("password");

  const onSubmit = handleSubmit(async (values) => {
    onError(null);
    try {
      await api("/api/v1/auth/register", {
        method: "POST",
        body: { email: values.email, password: values.password },
      });
      // Straight into the login half of the SAME dialog rather than a redirect
      // to a confirmation page — the account exists, the address is known, and
      // the next thing anyone wants is to be inside.
      onCreated(values.email.trim().toLowerCase());
    } catch (err) {
      onError(
        err instanceof ApiError
          ? apiErrorMessage(err, "Could not create your account.")
          : "Could not connect to the server.",
      );
    }
  });

  return (
    <>
      <form onSubmit={onSubmit} noValidate className="lpa-form">
        <div className="lpa-field">
          <label className="lpa-label" htmlFor="lpm-signup-email">
            Email address
          </label>
          <input
            id="lpm-signup-email"
            className="lpa-input"
            type="email"
            placeholder="you@company.com"
            autoComplete="email"
            autoFocus
            autoCapitalize="none"
            spellCheck={false}
            aria-label="Email address"
            aria-invalid={errors.email ? true : undefined}
            {...register("email")}
          />
          {errors.email && (
            <p className="lpa-error" role="alert">
              {errors.email.message}
            </p>
          )}
          <p className="lpa-hint">
            Used for logging in and for VAT invoices.
          </p>
        </div>

        <div className="lpa-field">
          <label className="lpa-label" htmlFor="lpm-signup-password">
            Password
          </label>
          <div className="lpa-input-wrap">
            <input
              id="lpm-signup-password"
              className="lpa-input"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              aria-label="Password"
              aria-invalid={errors.password ? true : undefined}
              {...register("password")}
            />
            <button
              type="button"
              className="lpa-eye"
              aria-pressed={showPassword}
              onClick={() => setShowPassword((v) => !v)}
            >
              <span className="lpa-sr-only">
                {showPassword ? "Hide password" : "Show password"}
              </span>
              <EyeIcon off={showPassword} />
            </button>
          </div>
          {errors.password && (
            <p className="lpa-error" role="alert">
              {errors.password.message}
            </p>
          )}
          <StrengthMeter password={passwordValue} />
        </div>

        <div className="lpa-field">
          <label className="lpa-label" htmlFor="lpm-signup-confirm">
            Confirm password
          </label>
          <div className="lpa-input-wrap">
            <input
              id="lpm-signup-confirm"
              className="lpa-input"
              type={showConfirm ? "text" : "password"}
              autoComplete="new-password"
              aria-label="Confirm password"
              aria-invalid={errors.passwordConfirm ? true : undefined}
              {...register("passwordConfirm")}
            />
            <button
              type="button"
              className="lpa-eye"
              aria-pressed={showConfirm}
              onClick={() => setShowConfirm((v) => !v)}
            >
              <span className="lpa-sr-only">
                {showConfirm ? "Hide password" : "Show password"}
              </span>
              <EyeIcon off={showConfirm} />
            </button>
          </div>
          {errors.passwordConfirm && (
            <p className="lpa-error" role="alert">
              {errors.passwordConfirm.message}
            </p>
          )}
        </div>

        <div className="lpa-field">
          <label className="lpa-checkline" htmlFor="lpm-signup-terms">
            <input
              id="lpm-signup-terms"
              className="lpa-checkbox"
              type="checkbox"
              checked={Boolean(acceptTerms)}
              onChange={(e) =>
                setValue("acceptTerms", e.currentTarget.checked as true, {
                  shouldValidate: true,
                })
              }
              aria-label="I accept the Terms of Service and Privacy Policy"
              aria-invalid={errors.acceptTerms ? true : undefined}
            />
            <span>
              I accept the{" "}
              <Link href="/terms" className="lpa-link">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link href="/privacy" className="lpa-link">
                Privacy Policy
              </Link>
              .
            </span>
          </label>
          {errors.acceptTerms && (
            <p className="lpa-error" role="alert">
              {errors.acceptTerms.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          className="lp-btn lp-btn--lime lpa-submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating account…" : "Create account"}
          {isSubmitting ? <SpinnerIcon /> : <ArrowIcon />}
        </button>
      </form>

      <footer className="lpm-foot">
        <p>
          <span className="lpa-foot-note">Already have an account?</span>
          <button
            type="button"
            className="lpa-link"
            onClick={() => onSwitchToLogin(getValues("email").trim().toLowerCase())}
          >
            Log in
          </button>
        </p>
      </footer>
    </>
  );
}

/* ---------------------------------------------------------------- stage 2 */

const RESEND_SECONDS = 30;

function CodeStage({
  email,
  onSignedIn,
  onError,
  onNotice,
  onChangeEmail,
  onUsePassword,
}: {
  email: string;
  onSignedIn: (data: SessionResponse) => void;
  onError: (message: string | null) => void;
  onNotice: (message: string | null) => void;
  onChangeEmail: () => void;
  onUsePassword: () => void;
}) {
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);
  const [resending, setResending] = useState(false);
  const submittedRef = useRef<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [cooldown]);

  const verify = useCallback(
    async (digits: string) => {
      if (isSubmitting || submittedRef.current === digits) return;
      submittedRef.current = digits;
      setIsSubmitting(true);
      setInvalid(false);
      onError(null);
      onNotice(null);
      try {
        const data = await api<SessionResponse>("/api/v1/auth/email-code/verify", {
          method: "POST",
          body: { email, code: digits },
        });
        onSignedIn(data);
      } catch (err) {
        setInvalid(true);
        setCode("");
        submittedRef.current = null;
        requestAnimationFrame(() => document.getElementById("lpm-code")?.focus());
        onError(
          err instanceof ApiError
            ? apiErrorMessage(err, "That code isn't right.")
            : "Could not connect to the server.",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, isSubmitting, onError, onNotice, onSignedIn],
  );

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    onError(null);
    try {
      await api("/api/v1/auth/email-code", { method: "POST", body: { email } });
      setCode("");
      setInvalid(false);
      submittedRef.current = null;
      setCooldown(RESEND_SECONDS);
      onNotice("A new code is on its way. The previous one no longer works.");
    } catch (err) {
      onError(
        err instanceof ApiError
          ? apiErrorMessage(err, "Could not resend the code.")
          : "Could not connect to the server.",
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (code.length === 6) void verify(code);
        }}
        noValidate
        className="lpa-form"
      >
        <div className="lpa-field">
          <label className="lpa-label" htmlFor="lpm-code">
            Login code
          </label>
          <CodeInput
            id="lpm-code"
            value={code}
            onChange={(next) => {
              setCode(next);
              if (invalid) setInvalid(false);
            }}
            onComplete={(digits) => void verify(digits)}
            disabled={isSubmitting}
            invalid={invalid}
            autoFocus
          />
          <p className="lpa-hint">
            Didn&apos;t get it? Check spam, or{" "}
            {cooldown > 0 ? (
              <span className="lpa-hint-strong">resend in {cooldown}s</span>
            ) : (
              <button
                type="button"
                className="lpa-link lpa-link--inline"
                onClick={() => void resend()}
                disabled={resending}
              >
                {resending ? "sending…" : "send a new code"}
              </button>
            )}
            .
          </p>
        </div>

        <button
          type="submit"
          className="lp-btn lp-btn--lime lpa-submit"
          disabled={isSubmitting || code.length !== 6}
        >
          {isSubmitting ? "Verifying…" : "Log in"}
          {isSubmitting ? <SpinnerIcon /> : <ArrowIcon />}
        </button>
      </form>

      <footer className="lpm-foot">
        <div className="lpm-foot-row">
          <button type="button" className="lpa-link" onClick={onChangeEmail}>
            Use a different email
          </button>
          <button type="button" className="lpa-link" onClick={onUsePassword}>
            Use a password
          </button>
        </div>
        <p className="lpa-foot-hint">
          Administrator accounts always log in with a password.
        </p>
      </footer>
    </>
  );
}

/* ---------------------------------------------------------------- stage 3 */

function PasswordStage({
  initialEmail,
  onSignedIn,
  onError,
  onBack,
}: {
  initialEmail: string;
  onSignedIn: (data: SessionResponse) => void;
  onError: (message: string | null) => void;
  onBack: (email: string) => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { email: initialEmail, password: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    onError(null);
    try {
      onSignedIn(
        await api<SessionResponse>("/api/v1/auth/login", {
          method: "POST",
          body: values,
        }),
      );
    } catch (err) {
      onError(
        err instanceof ApiError
          ? apiErrorMessage(err, "Could not log in.")
          : "Could not connect to the server.",
      );
    }
  });

  return (
    <>
      <form onSubmit={onSubmit} noValidate className="lpa-form">
        <div className="lpa-field">
          <label className="lpa-label" htmlFor="lpm-email-pw">
            Email address
          </label>
          <input
            id="lpm-email-pw"
            className="lpa-input"
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            aria-label="Email address"
            aria-invalid={errors.email ? true : undefined}
            {...register("email")}
          />
          {errors.email && (
            <p className="lpa-error" role="alert">
              {errors.email.message}
            </p>
          )}
        </div>

        <div className="lpa-field">
          <div className="lpa-label-row">
            <label className="lpa-label" htmlFor="lpm-password">
              Password
            </label>
            <Link href="/forgot-password" className="lpa-label-link">
              Forgot password?
            </Link>
          </div>
          <div className="lpa-input-wrap">
            <input
              id="lpm-password"
              className="lpa-input"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              autoFocus
              aria-label="Password"
              aria-invalid={errors.password ? true : undefined}
              {...register("password")}
            />
            <button
              type="button"
              className="lpa-eye"
              aria-pressed={showPassword}
              onClick={() => setShowPassword((v) => !v)}
            >
              <span className="lpa-sr-only">
                {showPassword ? "Hide password" : "Show password"}
              </span>
              <EyeIcon off={showPassword} />
            </button>
          </div>
          {errors.password && (
            <p className="lpa-error" role="alert">
              {errors.password.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          className="lp-btn lp-btn--lime lpa-submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Logging in…" : "Log in"}
          {isSubmitting ? <SpinnerIcon /> : <ArrowIcon />}
        </button>
      </form>

      <footer className="lpm-foot">
        <button
          type="button"
          className="lpa-link"
          onClick={() => onBack(getValues("email").trim().toLowerCase())}
        >
          ← Back to code login
        </button>
      </footer>
    </>
  );
}
